'use strict';

// ============================================================
// Utilities
// ============================================================

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

const formatMoney = (cents, format) => {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.theme?.moneyFormat || '{{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(number) || number === null) return '0';
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }

  let value = '';
  const match = formatString.match(placeholderRegex);
  if (!match) return String(cents);

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2); break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0); break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ','); break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' '); break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'"); break;
  }

  return formatString.replace(placeholderRegex, value);
};

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
const forceInt = (val, fallback = 0) => { const n = parseInt(val, 10); return isNaN(n) ? fallback : n; };

const KEYCODES = { TAB: 9, ESC: 27, DOWN: 40, RIGHT: 39, UP: 38, LEFT: 37, RETURN: 13 };
// Ecuador minimum order amount in Shopify cents.
const MIN_ORDER_AMOUNT = 5000;
const QUANTITY_DEBOUNCE_MS = 400;

const _str = (key, fallback) => window.purchaseOrder?.strings?.[key] || fallback;
const translateError = (key) => (window.purchaseOrder?.errors || {})[key] || key;
function _showLoader(el) { if (el) el.classList.add('is-visible'); }
function _hideLoader(el) { if (el) el.classList.remove('is-visible'); }

function getQuickOrderAssociateCustomerId() {
  const hidden = document.querySelector('[purchase-order-associate]');
  return String(hidden?.value || '').trim();
}

// Error for a cart request Shopify rejected. When the response carries a readable
// `description` (e.g. a cart validation Function message) it is flagged so the UI can
// give it more time on screen and resync the row quantities.
function cartRejectionError(data, fallbackMessage) {
  const error = new Error((data && data.description) || fallbackMessage);
  error.isCartRejection = !!(data && data.description);
  return error;
}

function isGanarMasCartItem(item) {
  const props = item?.properties || {};
  return props._ganar_mas === 'true' || props._ganar_mas === true ||
    props._ganar_mas_regalo === 'true' || props._ganar_mas_regalo === true;
}

function getGanarMasCustomerId(item) {
  const props = item?.properties || {};
  return String(props._ganar_mas_customer_id || '').trim();
}

function belongsToQuickOrderAssociate(item, customerId = getQuickOrderAssociateCustomerId()) {
  if (!isGanarMasCartItem(item)) return true;
  return !!customerId && getGanarMasCustomerId(item) === String(customerId);
}

function getQuickOrderCart(cart, customerId = getQuickOrderAssociateCustomerId()) {
  if (!cart) return cart;

  const items = (cart.items || []).filter(item => belongsToQuickOrderAssociate(item, customerId));
  const itemCount = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const subtotal = items.reduce((sum, item) => {
    return sum + (Number(item.final_line_price || item.line_price || item.original_line_price || 0) || 0);
  }, 0);

  return {
    ...cart,
    items,
    item_count: itemCount,
    items_subtotal_price: subtotal,
    original_total_price: subtotal,
    total_price: subtotal
  };
}

// --- Conditioned products ---
// Same rule as the cart and the bwec-checkout-extension `conditioned-validation` Function:
// a product tagged "conditioned" needs at least the minimum from the backend service in regular (non-conditioned)
// products, measured with offers applied. Only Associate / Distributor customers.
// Enforced here, before submitting: the backend does not reject the order, so Ganar Más and
// Regalón are never consumed by an order that gets refused.
const CONDITIONED_ROLES = ['Associate', 'Distributor'];

function isConditionedRuleActive() {
  return typeof TYPE_USER !== 'undefined' && CONDITIONED_ROLES.includes(TYPE_USER);
}

// The minimum always comes from the backend service (shop metafield custom.conditioned_amount),
// never from a theme fallback. null = the service has not answered yet or failed.
let conditionedServiceCents = null;

async function loadConditionedAmount() {
  if (conditionedServiceCents !== null) return conditionedServiceCents;
  try {
    const res = await window.backend.getConditionedAmount();
    const cents = Math.round(Number(res?.amount) * 100);
    if (cents > 0) conditionedServiceCents = cents;
  } catch (err) {
    console.error('[PO] Error reading the conditioned minimum from the service:', err);
  }
  return conditionedServiceCents;
}

function conditionedThresholdCents() {
  return conditionedServiceCents;
}

// Cart JSON has no product tags: the keys of the conditioned lines come from templates/cart.conditioned.liquid.
async function fetchConditionedLineKeys() {
  try {
    const res = await fetch(`${window.theme.routes.cart}?view=conditioned`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    const data = JSON.parse(await res.text());
    return new Set(Array.isArray(data.keys) ? data.keys : []);
  } catch (err) {
    console.error('[PO] Error reading conditioned cart lines:', err);
    return null;
  }
}

// Variants of conditioned rows seen in this quick order. Quick order starts with an empty cart, so
// every line comes from one of its rows: this does not depend on the ?view=conditioned request.
const conditionedVariantIds = new Set();

function registerConditionedRow(row) {
  if (row?.getAttribute('data-is-conditioned') !== 'true') return;
  const id = Number(row.getAttribute('data-variant-id'));
  if (id) conditionedVariantIds.add(id);
}

function isConditionedLine(item, conditionedKeys) {
  return conditionedKeys.has(item.key) || conditionedVariantIds.has(Number(item.variant_id ?? item.id));
}

function conditionedCartState(cart, conditionedKeys) {
  const items = cart?.items || [];
  const hasConditioned = items.some(item => isConditionedLine(item, conditionedKeys));
  const regularCents = items
    .filter(item => !isConditionedLine(item, conditionedKeys))
    .reduce((sum, item) => sum + (Number(item.final_line_price ?? item.line_price ?? 0) || 0), 0);
  const threshold = conditionedThresholdCents();
  return { hasConditioned, unmet: threshold === null || regularCents < threshold };
}

function conditionedAmountText() {
  return `$${(conditionedThresholdCents() / 100).toFixed(2)}`;
}

// Same text the cart, the checkout Function and order edit use.
const CONDITIONED_UNKNOWN_MESSAGE = 'No se pudo consultar el monto mínimo de compra para productos condicionados. Intenta de nuevo en unos segundos.';

function conditionedCheckoutMessage() {
  if (conditionedThresholdCents() === null) return CONDITIONED_UNKNOWN_MESSAGE;
  return `Tienes productos en tu carrito que requieren una compra mínima de ${conditionedAmountText()} en productos regulares. Agrega más productos o quita esos productos para continuar.`;
}

function conditionedAddMessage() {
  if (conditionedThresholdCents() === null) return CONDITIONED_UNKNOWN_MESSAGE;
  return `Este producto requiere una compra mínima de ${conditionedAmountText()} en productos regulares para poder agregarlo.`;
}

// While the regular products do not reach the minimum, the add (+) button of every conditioned
// row stays inactive and the row says why. Decreasing / removing is still allowed.
let conditionedAddLocked = false;

function applyConditionedRowLocks() {
  document.querySelectorAll('[data-js-purchase-order-search-form-row][data-is-conditioned="true"]').forEach((row) => {
    registerConditionedRow(row);
    const plus = row.querySelector('.quantity__button--plus');
    const input = row.querySelector('.quantity__input');
    const message = row.querySelector('[data-js-conditioned-row-message]');

    // Remember the state set by stock / kit rules so unlocking never re-enables a disabled row.
    [plus, input].forEach((el) => {
      if (el && el.dataset.baseDisabled === undefined) el.dataset.baseDisabled = String(el.disabled);
    });

    if (plus) {
      plus.disabled = plus.dataset.baseDisabled === 'true' || conditionedAddLocked;
      plus.classList.toggle('is-conditioned-locked', conditionedAddLocked);
    }
    if (input) {
      const empty = (parseInt(input.value, 10) || 0) === 0;
      input.disabled = input.dataset.baseDisabled === 'true' || (conditionedAddLocked && empty);
    }
    if (message) {
      const text = message.querySelector('span');
      if (text) text.textContent = conditionedAddLocked ? conditionedAddMessage() : '';
      message.style.display = conditionedAddLocked ? '' : 'none';
    }
  });
}

function clearQuickOrderCartOnExit() {
  const clearUrl = `${window.theme?.routes?.cart || '/cart'}/clear.js`;

  try {
    fetch(clearUrl, { method: 'POST', keepalive: true }).catch(() => {});
  } catch(e) {
    // Best effort cleanup during navigation.
  }
}


// ============================================================
// CartService — Promise-chain sequential queue
// ============================================================

const hideLoader = (loader) => {
  if (loader) loader.classList.remove('is-visible');
  window.purchaseOrder.locked = false;
  document.dispatchEvent(new CustomEvent('quantityInput:lock', { detail: { locked: false }}));
}

document.addEventListener('cartTransformCompleted:lock', (evt) => {
    const submitBtn = document.querySelector('[data-js-purchase-order-form-submit]');
    const associateInput = document.querySelector('[name="purchase_order[partner]"]');
    if (!submitBtn || !associateInput) return;

    if (evt.detail.completed) {
      if (window.activeLoader) { 
        hideLoader(window.activeLoader);
        window.activeLoader = null;
      };

      if (!associateInput.value || window.cartTotal < MIN_ORDER_AMOUNT || window.isCartLoading) {
        document.dispatchEvent(new CustomEvent('purchaseOrder:lock', { detail: { locked: true }}));
      }  else {
        document.dispatchEvent(new CustomEvent('purchaseOrder:lock', { detail: { locked: false }}));
      }
    } else { 
      document.dispatchEvent(new CustomEvent('purchaseOrder:lock', { detail: { locked: true }}));
    }
});

// Sequential interval execution - waits for previous execution to complete
const runSequentialInterval = async () => {
  if (operationQueue.length) {
    // get the first operation from the queue and add it to the operations to commit
    const operation = operationQueue.shift();

    // Execute operation but preserve the original callback
    const executedOperation = await executeOperation(operation);
    executedOperation.callback = operation.callback;

    // add operation to the operations to commit
    operationsToCommit.push(executedOperation);

    // return to avoid executing the next operation
    return;
  }

  if (operationsToCommit.length > 0) {
    // once all operations are executed, commit them
    await commitCartOperations();

  }
}
class CartService {
  constructor() {
    this._queue = Promise.resolve();
    this._cart = null;
  }

  get cart() { return this._cart; }

  _enqueue(fn) {
    const task = this._queue.then(fn).catch(err => { console.error('[CartService]', err); throw err; });
    // Keep the chain alive after a failure: a rejected promise stored as the queue would
    // make every later operation skip its callback and re-throw the same old error.
    this._queue = task.catch(() => {});
    return task;
  }

  async fetchCart() {
    const res = await fetch(window.theme.routes.cart + '.js');
    if (!res.ok) throw new Error('Failed to fetch cart');
    this._cart = await res.json();
    return this._cart;
  }

  async _changeQuantity(identifier, quantity) {
    const res = await fetch(`${window.theme.routes.cart}/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(identifier), quantity })
    });
    const data = await res.json();
    if (!data.items) throw cartRejectionError(data, 'Error al actualizar el carrito');
    this._cart = data;
    return data;
  }

  async _addVariantQuantity(variantId, quantity) {
    const res = await fetch(`${window.theme.routes.cart}/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] })
    });
    const data = await res.json();
    if (data.status === 422) throw cartRejectionError(data, 'No se pudo agregar el producto');
    this._cart = await this.fetchCart();
    return this._cart;
  }

  async _setSplitVariantQuantity(variantId, quantity, matchingItems) {
    const currentTotal = matchingItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const delta = quantity - currentTotal;

    if (delta > 0) {
      await this._addVariantQuantity(variantId, delta);
      return;
    }

    if (delta === 0) return;

    let unitsToRemove = Math.abs(delta);
    const itemsForReduction = [...matchingItems].sort((a, b) => {
      const aHasDiscount = (a.line_level_discount_allocations || []).length > 0;
      const bHasDiscount = (b.line_level_discount_allocations || []).length > 0;
      if (aHasDiscount === bHasDiscount) return 0;
      // Remove units from non-discounted lines first, preserving discounted ones when possible.
      return aHasDiscount ? 1 : -1;
    });

    for (const item of itemsForReduction) {
      if (unitsToRemove <= 0) break;
      const removeFromLine = Math.min(item.quantity, unitsToRemove);
      const nextQty = item.quantity - removeFromLine;
      await this._changeQuantity(item.key || item.id, nextQty);
      unitsToRemove -= removeFromLine;
    }
  }

  setQuantity(variantId, quantity) {
    return this._enqueue(async () => {
      const matchingItems = (this._cart?.items || []).filter(i => i.id === variantId);
      const isInCart = matchingItems.length > 0;

      if (quantity > 0) {
        if (matchingItems.length > 1) {
          await this._setSplitVariantQuantity(variantId, quantity, matchingItems);
          this._cart = await this.fetchCart();
        } else if (isInCart) {
          await this._changeQuantity(variantId, quantity);
        } else {
          await this._addVariantQuantity(variantId, quantity);
        }
      } else if (matchingItems.length > 1) {
        await this._setSplitVariantQuantity(variantId, 0, matchingItems);
        this._cart = await this.fetchCart();
      } else if (isInCart) {
        await this._changeQuantity(variantId, 0);
      }

      this._fireChange();
      return this._cart;
    });
  }

  clear() {
    return this._enqueue(async () => {
      const res = await fetch(`${window.theme.routes.cart}/clear.js`, { method: 'POST' });
      if (!res.ok) throw new Error('Error al vaciar el carrito');
      this._cart = await res.json();
      this._fireChange();
      return this._cart;
    });
  }

  removeItems(items) {
    return this._enqueue(async () => {
      const keys = (items || []).map(item => item?.key).filter(Boolean);
      if (!keys.length) {
        this._cart = await this.fetchCart();
        this._fireChange();
        return this._cart;
      }

      for (const key of keys) {
        await this._changeQuantity(key, 0);
      }

      this._cart = await this.fetchCart();
      this._fireChange();
      return this._cart;
    });
  }

  _fireChange() {
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: this._cart }, bubbles: true }));
  }
}

const cartService = new CartService();


// ============================================================
// PurchaseOrder — Main orchestrator
// ============================================================

if (!customElements.get('purchase-order')) {
  class PurchaseOrder extends HTMLElement {
    constructor() {
      super();

      this.shippingInput = this.querySelector('[name="purchase_order[shipping_type]"]');
      this.associateInput = this.querySelector('[name="purchase_order[partner]"]');
      this.associateHidden = this.querySelector('[purchase-order-associate]');
      this.partnerLoader = this.querySelector('[data-js-purchase-order-partner-update-loader]');
      this.partnerError = this.querySelector('[data-js-purchase-order-partner-update-error]');
      this.partnerSuccess = this.querySelector('[data-js-purchase-order-partner-update-success]');
      this.partnerSuccessText = this.querySelector('[data-js-purchase-order-partner-update-success-text]');

      this.bar = document.querySelector('[data-js-purchase-order-form-bar]');
      this.submitBtn = this.bar?.querySelector('[data-js-purchase-order-form-submit]');
      this.countEl = this.bar?.querySelector('[data-js-purchase-order-form-count]');
      this.totalQtyEl = this.bar?.querySelector('[data-js-purchase-order-form-total-quantity]');
      this.subtotalEl = this.bar?.querySelector('[data-js-purchase-order-form-subtotal-init]');
      this.minOrderEl = this.bar?.querySelector('[data-js-min-order-message], [data-js-purchase-order-minimum-banner]');
      this.conditionedEl = this.bar?.querySelector('[data-js-conditioned-message]');
      this.minicartToggle = this.querySelector('[data-js-po-minicart-toggle]');
      this.minicartPanel = this.querySelector('[data-js-po-minicart-panel]');
      this.minicartBody = this.querySelector('[data-js-po-minicart-body]');
      this.minicartCountEls = this.querySelectorAll('[data-js-po-minicart-count]');

      this.toast = document.querySelector('[data-js-purchase-order-toast]');
      this.toastError = this.toast?.querySelector('[data-js-purchase-order-toast-text="error"]');
      this.toastSuccess = this.toast?.querySelector('[data-js-purchase-order-toast-text="success"]');

      this.associateCode = this.associateInput?.value?.trim() || null;
      this.associate = null;
      this.submitting = false;
      this._toastTimer = null;
      this._submitTimeout = null;
      this._ganarMasFallbackTimer = null;
      this._waitingGanarMas = false;
      this._submitInFlight = false;
      this._busyCount = 0;
      this._conditionedBlocked = false;
      this._conditionedPending = false;
      this._conditionedRun = 0;
      this._minicartLoading = false;
      this._minicartNeedsRefresh = false;

      this._init();
    }

    async _init() {
      await cartService.fetchCart();
      if ((cartService.cart?.items || []).length) {
        await cartService.clear();
      }

      this._updateBar();
      this._bindPageCleanup();

      document.addEventListener('theme:cart:change', (e) => {
        if (e.detail?.cart) cartService._cart = e.detail.cart;
        this._updateBar();
        if (this._isMinicartOpen()) {
          this._loadMinicart();
        }
        this._syncRegalonGift();
      });

      document.addEventListener('ganarMas:cartSynced', (e) => {
        if (e.detail?.cart) {
          cartService._cart = e.detail.cart;
          this._updateBar();
        } else {
          cartService.fetchCart().then(() => this._updateBar()).catch(() => {});
        }

        if (this._isMinicartOpen()) {
          this._loadMinicart();
        }
      });

      document.addEventListener('regalon:cartSynced', (e) => {
        if (e.detail?.cart) {
          cartService._cart = e.detail.cart;
          this._updateBar();
        } else {
          cartService.fetchCart().then(() => this._updateBar()).catch(() => {});
        }

        if (this._isMinicartOpen()) {
          this._loadMinicart();
        }
      });

      document.addEventListener('ganarMas:validationFailed', () => {
        clearTimeout(this._submitTimeout);
        clearTimeout(this._ganarMasFallbackTimer);
        this._waitingGanarMas = false;
        this.submitting = false;
        this.submitBtn?.classList.remove('loading');
        this._updateSubmitState();
      });

      document.addEventListener('purchaseOrder:busy', (e) => {
        this._busyCount += e.detail.busy ? 1 : -1;
        if (this._busyCount < 0) this._busyCount = 0;
        this.classList.toggle('is-busy', this._busyCount > 0);
        this._updateSubmitState();
      });

      document.addEventListener('ganarMas:continueCheckout', (e) => {
        this._waitingGanarMas = false;
        clearTimeout(this._ganarMasFallbackTimer);
        clearTimeout(this._submitTimeout);
        this.submitting = true;
        this._submitOrder(e);
      });

      const associateHandler = debounce((e) => this._onAssociateChange(e), 500);
      this.associateInput?.addEventListener('keyup', associateHandler);
      this.associateInput?.addEventListener('input', (e) => {
        this._associateValidationId = (this._associateValidationId || 0) + 1;
        this.associate = null;
        this.associateCode = null;
        if (this.associateHidden) this.associateHidden.value = '';
        this._setPartnerUI('idle');
        this._filterMinicartRows();
        this._updateSubmitState();
        associateHandler(e);
      });

      this.submitBtn?.addEventListener('click', (e) => this._onCheckGanarMas(e));
      this.minicartToggle?.addEventListener('click', () => this._toggleMinicart());

      if (this.associateCode) {
        this._triggerAssociateValidation();
      }
    }

    _bindPageCleanup() {
      if (this._pageCleanupBound) return;
      this._pageCleanupBound = true;

      window.addEventListener('pagehide', clearQuickOrderCartOnExit);
      window.addEventListener('beforeunload', clearQuickOrderCartOnExit);
    }

    _triggerAssociateValidation() {
      this._onAssociateChange({ target: this.associateInput });
    }

    _associateCustomerId() {
      return String(this.associateHidden?.value || '').trim();
    }

    _activeCart() {
      return getQuickOrderCart(cartService.cart, this._associateCustomerId());
    }

    // --- Associate validation ---

    _normalizeAssociateData(associateResponse, associateCode) {
      const source = associateResponse?.associate || associateResponse?.data || associateResponse || {};
      const rawId = source.id || source.customerId || source.customer_id || source.associateId || source.distributorId || '';
      const rawName = source.displayName || source.name || source.fullName || source.nombre || source.associateName || source.distributorName || '';
      const normalizedId = String(rawId || '').split('/').pop() || String(associateCode || '');
      const normalizedName = String(rawName || '').trim() || String(associateCode || '');

      return {
        ...source,
        id: normalizedId,
        name: normalizedName
      };
    }

    async _onAssociateChange(e) {
      const input = e.target;
      const value = input.value.trim();
      const validationId = this._associateValidationId = (this._associateValidationId || 0) + 1;
      this.associate = null;
      this.associateCode = null;
      if (this.associateHidden) this.associateHidden.value = '';
      this._updateSubmitState();
      input.classList.remove('has-error');

      if (!value) {
        this.associate = null;
        this.associateCode = null;
        if (this.associateHidden) this.associateHidden.value = '';
        this._setPartnerUI('idle');
        this._filterMinicartRows();
        this._updateSubmitState();
        window.RegalonCheckout?.removeGifts?.();
        return;
      }

      this._setPartnerUI('loading');

      try {
        const associateResponse = await window.backend.getAssociate(value);
        if (validationId !== this._associateValidationId || input.value.trim() !== value) return;
        this.associate = this._normalizeAssociateData(associateResponse, value);
        this.associateCode = value;
        if (this.associateHidden) {
          this.associateHidden.value = this.associate.id || '';
        }
        this._setPartnerUI('success', this.associate.name);
        this._filterMinicartRows();
        this._syncRegalonGift();
      } catch (err) {
        if (validationId !== this._associateValidationId || input.value.trim() !== value) return;
        this.associate = null;
        this.associateCode = null;
        if (this.associateHidden) this.associateHidden.value = '';
        let msg = err.message || '';
        try {
          const parsed = JSON.parse(msg);
          msg = parsed?.error || msg;
        } catch (e) { /* keep original message */ }
        const key = msg.includes('Asociado no encontrado') ? 'Associate not found' : msg;
        this._setPartnerUI('error', translateError(key));
        input.classList.add('has-error');
        this._filterMinicartRows();
        window.RegalonCheckout?.removeGifts?.();
      }

      this._updateSubmitState();
    }

    _setPartnerUI(state, text = '') {
      const isLoading = state === 'loading';
      const isError = state === 'error';
      const isSuccess = state === 'success';

      if (this.partnerLoader) this.partnerLoader.classList.toggle('is-visible', isLoading);
      if (this.partnerError) {
        this.partnerError.classList.toggle('is-visible', isError);
        if (isError) this.partnerError.textContent = text;
      }
      if (this.partnerSuccess) {
        this.partnerSuccess.classList.toggle('is-visible', isSuccess);
      }
      if (this.partnerSuccessText) {
        this.partnerSuccessText.textContent = isSuccess ? text : '';
      }
      try {
        window.GANARMAS_ASSOCIATE_DISPLAY_NAME = isSuccess ? text : '';
      } catch (e) { /* ignore */ }
    }

    _syncRegalonGift() {
      if (this._regalonSyncTimer) clearTimeout(this._regalonSyncTimer);
      this._regalonSyncTimer = setTimeout(async () => {
        try {
          const associateId = this._associateCustomerId();
          if (!this.associate || !associateId) {
            await window.RegalonCheckout?.removeGifts?.();
            return;
          }
          const currentCart = this._activeCart();
          const subtotal = currentCart?.items_subtotal_price || 0;
          if (subtotal >= MIN_ORDER_AMOUNT) {
            await window.RegalonCheckout?.prepare({ customerId: associateId });
          } else {
            await window.RegalonCheckout?.removeGifts?.();
          }
        } catch (err) {
          console.error('[PO] Regalon auto-sync error:', err);
        }
      }, 400);
    }

    // --- Bar state ---

    _updateBar() {
      const c = this._activeCart();
      if (!c) return;

      const items = c.items || [];
      const count = items.length;
      const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
      const subtotal = c.items_subtotal_price || 0;

      window.cartTotal = subtotal;

      if (this.countEl) this.countEl.textContent = count;
      if (this.totalQtyEl) this.totalQtyEl.textContent = totalQty;
      if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(subtotal, window.theme?.moneyFormat);
      this.minicartCountEls.forEach((el) => {
        el.textContent = count;
      });

      this.bar?.classList.toggle('active', count > 0);
      this._refreshConditioned(c);

      if (this.minOrderEl) {
        this.minOrderEl.classList.toggle('is-visible', subtotal < MIN_ORDER_AMOUNT && count > 0);
      }

      this._updateSubmitState();
    }

    _isMinicartOpen() {
      return !!this.minicartPanel && !this.minicartPanel.hidden;
    }

    async _toggleMinicart() {
      if (!this.minicartPanel || !this.minicartToggle) return;

      if (this._isMinicartOpen()) {
        this.minicartPanel.hidden = true;
        this.minicartToggle.setAttribute('aria-expanded', 'false');
        this.minicartToggle.classList.remove('is-active');
        return;
      }

      this.minicartPanel.hidden = false;
      this.minicartToggle.setAttribute('aria-expanded', 'true');
      this.minicartToggle.classList.add('is-active');
      await this._loadMinicart();
    }

    async _loadMinicart() {
      if (!this.minicartBody || !this._isMinicartOpen()) return;

      if (this._minicartLoading) {
        this._minicartNeedsRefresh = true;
        return;
      }

      this._minicartLoading = true;
      this.minicartPanel?.classList.add('is-loading');
      this.minicartBody.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch(`${window.theme.routes.root_url}?section_id=api-cart-items-readonly`);
        if (!res.ok) throw new Error('Failed to load minicart');

        const html = await res.text();
        const doc = document.createElement('div');
        doc.innerHTML = html;

        const apiContent = doc.querySelector('[data-api-content]');
        if (apiContent) {
          this.minicartBody.innerHTML = apiContent.innerHTML;
          this._filterMinicartRows();
        }
      } catch (err) {
        console.error('[PO] Minicart refresh failed:', err);
      } finally {
        this._minicartLoading = false;
        this.minicartPanel?.classList.remove('is-loading');
        this.minicartBody.removeAttribute('aria-busy');

        if (this._minicartNeedsRefresh) {
          this._minicartNeedsRefresh = false;
          if (this._isMinicartOpen()) {
            this._loadMinicart();
          }
        }
      }
    }

    _filterMinicartRows() {
      if (!this.minicartBody) return;

      const associateCustomerId = this._associateCustomerId();
      this.minicartBody.querySelectorAll('[data-ganar-mas-cart-item="true"]').forEach((row) => {
        const lineCustomerId = String(row.getAttribute('data-ganar-mas-customer-id') || '').trim();
        const canShow = !!associateCustomerId && lineCustomerId === associateCustomerId;
        row.toggleAttribute('hidden', !canShow);
      });
    }

    // Locks the submit button (and says why) when the cart holds conditioned products but
    // the regular products do not reach the configured minimum.
    async _refreshConditioned(cart) {
      const run = ++this._conditionedRun;
      const hasItems = (cart?.items || []).length > 0;

      // Synchronously, before any await: the submit button must not flash enabled while the
      // state is recalculated after a cart change.
      this._conditionedPending = isConditionedRuleActive() && hasItems;
      this._updateSubmitState();

      if (isConditionedRuleActive()) await loadConditionedAmount();
      if (run !== this._conditionedRun) return;

      if (!isConditionedRuleActive()) {
        this._conditionedPending = false;
        conditionedAddLocked = false;
        applyConditionedRowLocks();
        this._setConditionedBlocked(false);
        return;
      }

      // An empty cart has no regular amount yet: conditioned rows stay locked, nothing to submit.
      if (!hasItems) {
        this._conditionedPending = false;
        const threshold = conditionedThresholdCents();
        conditionedAddLocked = threshold === null || threshold > 0;
        applyConditionedRowLocks();
        this._setConditionedBlocked(false);
        return;
      }

      const keys = (await fetchConditionedLineKeys()) || new Set();
      if (run !== this._conditionedRun) return;
      this._conditionedPending = false;

      const state = conditionedCartState(cart, keys);
      conditionedAddLocked = state.unmet;
      applyConditionedRowLocks();
      this._setConditionedBlocked(state.hasConditioned && state.unmet);
    }

    _setConditionedBlocked(blocked) {
      this._conditionedBlocked = blocked;
      if (this.conditionedEl) {
        this.conditionedEl.textContent = blocked ? conditionedCheckoutMessage() : '';
        this.conditionedEl.classList.toggle('is-visible', blocked);
      }
      this._updateSubmitState();
    }

    _updateSubmitState() {
      const subtotal = this._activeCart()?.items_subtotal_price || 0;
      const isBusy = this._busyCount > 0;
      const canSubmit = !!this.associateCode && subtotal >= MIN_ORDER_AMOUNT && !this.submitting && !isBusy
        && !this._conditionedBlocked && !this._conditionedPending;

      if (this.submitBtn) {
        this.submitBtn.toggleAttribute('disabled', !canSubmit);
        this.submitBtn.classList.toggle('disabled', !canSubmit);
      }
    }

    // --- Submit flow ---

    async _onCheckGanarMas(e) {
      e.preventDefault();
      if (this.submitting || !this.associate || this.associateCode !== this.associateInput?.value.trim()) return;

      this.submitting = true;
      this._waitingGanarMas = false;
      this._updateSubmitState();
      this.submitBtn?.classList.add('loading');

      try {
        await window.RegalonCheckout?.prepare({ customerId: this._associateCustomerId() });
      } catch (regalonErr) {
        console.error('[PO] Regalon prepare failed before checkout check:', regalonErr);
      }

      const hasGanarMasQuickOrder = !!(
        window.__ganarMasQuickOrderReady &&
        document.querySelector('[data-ganamas-root][data-ganamas-quick-order="true"]')
      );

      if (!hasGanarMasQuickOrder) {
        await this._submitOrder(e);
        return;
      }

      this._waitingGanarMas = true;

      // Do not time out while the Ganar Más gift modal is open — users need time to choose;
      // otherwise submitting becomes false, the error toast shows, and Continuar no-ops in _submitOrder.
      this._submitTimeout = setTimeout(() => {
        if (!this.submitting || this._waitingGanarMas) return;
        this.submitting = false;
        this.submitBtn?.classList.remove('loading');
        this._updateSubmitState();
        this._showToast('error');
      }, 30000);

      try {
        document.dispatchEvent(new CustomEvent('ganarMas:calculate', { cancelable: true }));
      } catch (err) {
        clearTimeout(this._submitTimeout);
        clearTimeout(this._ganarMasFallbackTimer);
        this.submitBtn?.classList.remove('loading');
        this._showToast('error');
        this.submitting = false;
        this._waitingGanarMas = false;
        this._updateSubmitState();
      }
    }

    async _submitOrder(e) {
      e?.preventDefault?.();
      if (!this.submitting || this._submitInFlight || !this.associate || this.associateCode !== this.associateInput?.value.trim()) return;
      this._submitInFlight = true;
      this._waitingGanarMas = false;
      clearTimeout(this._submitTimeout);
      clearTimeout(this._ganarMasFallbackTimer);

      try {
        await window.RegalonCheckout?.prepare({ customerId: this._associateCustomerId() });
        const rawCart = typeof window.__sanitizeGanarMasCart === 'function'
          ? await window.__sanitizeGanarMasCart({ customerId: this._associateCustomerId() })
          : await cartService.fetchCart();
        const freshCart = getQuickOrderCart(rawCart, this._associateCustomerId());

        const payload = {
          shipping: this.shippingInput?.value || 'Standard',
          cart: freshCart
        };

        const orderSummary = {
          itemCount: freshCart.items?.length || 0,
          totalQty: (freshCart.items || []).reduce((s, i) => s + i.quantity, 0),
          subtotal: freshCart.items_subtotal_price || 0,
          associateName: this.associate?.name || this.associateCode
        };

        const order = await window.backend.createAssociateOrder(this.associateCode, payload);

        this.submitBtn?.classList.remove('loading');
        this.submitBtn?.classList.add('has-success');

        const orderId = order?.complete_order?.orderId;
        orderSummary.orderId = orderId;

        await cartService.clear();
        document.dispatchEvent(new CustomEvent('ganarMas:orderCompleted', {
          detail: { customerId: this._associateCustomerId() }
        }));

        this._showSuccessSummary(orderSummary);

        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (err) {
        console.error('[PO] Order failed:', err);
        let message = err.message || '';
        try { message = JSON.parse(message).error || message; } catch (_) { /* plain text response */ }
        if (message.includes('El asociado está inactivo')) {
          this.associate = null;
          this.associateCode = null;
          if (this.associateHidden) this.associateHidden.value = '';
          this._setPartnerUI('error', message);
          this.associateInput?.classList.add('has-error');
        }
        await this._removeAssociateGanarMasCartItems().catch((cleanupErr) => {
          console.error('[PO] Failed to clean Ganar Mas quick-order item after order error:', cleanupErr);
        });
        this._showToast('error');
        this.submitBtn?.classList.remove('loading');
      } finally {
        this._submitInFlight = false;
        setTimeout(() => {
          this.submitting = false;
          this.submitBtn?.classList.remove('has-success');
          this._updateSubmitState();
        }, 1500);
      }
    }

    async _removeAssociateGanarMasCartItems() {
      const cart = await cartService.fetchCart();
      const associateCustomerId = this._associateCustomerId();
      const items = (cart.items || []).filter(item => {
        return isGanarMasCartItem(item) && belongsToQuickOrderAssociate(item, associateCustomerId);
      });

      if (!items.length) return;
      await cartService.removeItems(items);
    }

    // --- Success Summary ---

    _showSuccessSummary(summary) {
      this._hideToast();
      document.querySelector('[data-js-success-summary]')?.remove();

      const subtotalFormatted = formatMoney(summary.subtotal, window.theme?.moneyFormat);
      const orderLabel = summary.orderId ? `#${summary.orderId}` : '';
      const ctaLabel = _str('new_order_cta', 'Crear nuevo pedido');

      this.classList.add('is-order-complete');
      document.documentElement.classList.add('quick-order-success-lock');
      document.body.classList.add('quick-order-success');

      const scrollY = window.scrollY;
      document.body.dataset.poScrollLock = String(scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      const el = document.createElement('div');
      el.className = 'po-success-summary po-success-summary--blocking';
      el.setAttribute('data-js-success-summary', '');
      el.innerHTML = `
        <div class="po-success-summary__panel">
          <div class="po-success-summary__icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <h3 class="po-success-summary__title">${_str('success_title', '¡Pedido creado exitosamente!')} ${orderLabel}</h3>
          <div class="po-success-summary__details">
            <div class="po-success-summary__detail">
              <span class="po-success-summary__label">${_str('associate_label', 'Asociado')}</span>
              <span class="po-success-summary__value">${summary.associateName || '—'}</span>
            </div>
            <div class="po-success-summary__detail">
              <span class="po-success-summary__label">${_str('products_label', 'Productos')}</span>
              <span class="po-success-summary__value">${summary.itemCount} ${_str('sku_unit', 'códigos')} · ${summary.totalQty} ${_str('units_label', 'unidades')}</span>
            </div>
            <div class="po-success-summary__detail">
              <span class="po-success-summary__label">${_str('subtotal_label', 'Subtotal')}</span>
              <span class="po-success-summary__value">${subtotalFormatted}</span>
            </div>
          </div>
          <button type="button" class="btn btn--primary po-success-summary__cta" data-js-success-new-order="true">
            ${ctaLabel}
          </button>
        </div>
      `;

      document.body.appendChild(el);

      const onNewOrder = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._beginNewQuickOrder();
      };
      el.querySelector('[data-js-success-new-order]')?.addEventListener('click', onNewOrder, { capture: true });
    }

    _beginNewQuickOrder() {
      const btn = document.querySelector('[data-js-success-new-order]');
      if (btn) {
        btn.disabled = true;
        btn.classList.add('btn--loading');
      }
      cartService.clear().catch(() => {});
      window.location.reload();
    }

    // --- Toast ---

    _showToast(type) {
      if (!this.toast) return;

      clearTimeout(this._toastTimer);

      this.toast.classList.add('is-visible');
      this.toast.classList.toggle('has-error', type === 'error');
      this.toast.classList.toggle('has-success', type === 'success');

      if (type === 'error') {
        if (this.toastError) {
          this.toastError.textContent = _str('error_message', 'Error al crear el pedido. Intenta de nuevo.');
          this.toastError.classList.remove('hide');
        }
        if (this.toastSuccess) { this.toastSuccess.textContent = ''; this.toastSuccess.classList.add('hide'); }
      }

      this._toastTimer = setTimeout(() => this._hideToast(), 6000);
    }

    _hideToast() {
      if (!this.toast) return;
      this.toast.classList.remove('is-visible', 'has-error', 'has-success');
      if (this.toastError) { this.toastError.textContent = ''; this.toastError.classList.add('hide'); }
      if (this.toastSuccess) { this.toastSuccess.textContent = ''; this.toastSuccess.classList.add('hide'); }
    }
  }

  customElements.define('purchase-order', PurchaseOrder);
}


// ============================================================
// PurchaseOrderSearchForm — Quantity changes & cart ops
//
// Debounces rapid clicks (400ms) then locks all inputs during
// the cart operation. No more jumping bar values.
// ============================================================

if (!customElements.get('purchase-order-search-form')) {
  class PurchaseOrderSearchForm extends HTMLElement {
    constructor() {
      super();
      this._processing = false;
      this._pendingSync = false;
      this._debounceTimers = new Map();
      this._cartReady = false;
      this._init();
    }

    _init() {
      this._cartReady = !!cartService.cart;

      this.addEventListener('QuickOrder:quantity:change', (e) => {
        this._onQuantityChange(e);
      });

      document.addEventListener('theme:cart:change', () => {
        this._cartReady = true;
        if (this._processing) {
          this._pendingSync = true;
          return;
        }
        this._syncQuantities();
      });

      document.addEventListener('purchaseOrder:searchResultsUpdated', () => {
        this._syncQuantities();
      });

      if (this._cartReady) {
        requestAnimationFrame(() => this._syncQuantities());
      }
    }

    _syncQuantities() {
      if (!this._cartReady) return;
      const items = getQuickOrderCart(cartService.cart)?.items || [];
      const rows = this.querySelectorAll('[data-js-purchase-order-search-form-row]');

      rows.forEach(row => {
        const variantId = parseFloat(row.getAttribute('data-variant-id'));
        const input = row.querySelector('[data-js-purchase-order-quantity-input]');
        if (!input || document.activeElement === input) return;

        const nextQty = items
          .filter(i => i.id === variantId)
          .reduce((sum, i) => sum + (i.quantity || 0), 0);
        input.value = nextQty;
        const selector = row.querySelector('quantity-selector');
        if (selector?.syncFromExternal) {
          selector.syncFromExternal(nextQty);
        }
      });
    }

    _onQuantityChange(e) {
      const input = e.target;
      const row = input.closest('[data-js-purchase-order-search-form-row]');
      if (!row) return;

      const variantId = parseFloat(row.getAttribute('data-variant-id'));

      const existing = this._debounceTimers.get(variantId);
      if (existing) clearTimeout(existing);

      this._debounceTimers.set(variantId, setTimeout(() => {
        this._debounceTimers.delete(variantId);
        this._commitQuantityChange(row, input, variantId);
      }, QUANTITY_DEBOUNCE_MS));
    }

    async _commitQuantityChange(row, input, variantId) {
      const productId = parseFloat(row.getAttribute('data-product-id'));
      const pieceLimit = parseInt(row.getAttribute('data-piece-limit'), 10) || 0;
      const loader = row.querySelector('[data-js-purchase-order-update-loader]');
      const errorRow = row.querySelector('.quantity__wrapper_error');

      let quantity = forceInt(input.value, 0);
      if (isNaN(variantId)) return;

      const associateId = document.querySelector('[purchase-order-associate]')?.value || '';

      if (quantity > 0 && productId && associateId) {
        try {
          const limitRes = await fetch(
            `${AWS_API_URL}/Prod/products/${productId}/check-limit?quantity=${quantity}&customerId=${associateId}`,
            { method: 'GET', headers: { 'Content-Type': 'application/json' } }
          );
          const limitData = await limitRes.json();
          if (!limitRes.ok || (limitData && limitData.result)) {
            this._showRowError('Has superado la cantidad máxima semanal para este producto.', errorRow);
            if (pieceLimit > 0) {
              quantity = pieceLimit;
              input.value = quantity;
            }
          }
        } catch (err) {
          console.error('[PO] Error checking product limit:', err);
        }
      }

      registerConditionedRow(row);
      if (quantity > 0 && row.getAttribute('data-is-conditioned') === 'true' && isConditionedRuleActive()) {
        const currentQty = (cartService.cart?.items || [])
          .filter(item => item.id === variantId)
          .reduce((sum, item) => sum + (item.quantity || 0), 0);

        if (quantity > currentQty && !(await this._canAddConditioned())) {
          this._showRowError(conditionedAddMessage(), errorRow, 8000);
          this._syncQuantities();
          return;
        }
      }

      this._processing = true;
      document.dispatchEvent(new CustomEvent('purchaseOrder:busy', { detail: { busy: true } }));
      _showLoader(loader);

      try {
        await cartService.setQuantity(variantId, quantity);
      } catch (err) {
        console.error('[PO] Cart operation failed:', err);
        // Cart validation messages are long: leave them on screen longer.
        this._showRowError(
          err.message ? err.message : 'Error al actualizar el carrito. Intenta de nuevo.',
          errorRow,
          err.isCartRejection ? 8000 : 3000
        );
        // The change was rejected, so the cart kept its previous quantities: put the
        // row inputs back instead of leaving the number that was typed.
        if (err.isCartRejection) this._syncQuantities();
      } finally {
        _hideLoader(loader);
        document.dispatchEvent(new CustomEvent('purchaseOrder:busy', { detail: { busy: false } }));
        this._processing = false;

        if (this._pendingSync) {
          this._pendingSync = false;
          this._syncQuantities();
        }
      }
    }

    // A conditioned product can only be added while the regular products already reach the minimum.
    async _canAddConditioned() {
      if ((await loadConditionedAmount()) === null) return false; // minimum unknown: do not add
      const keys = (await fetchConditionedLineKeys()) || new Set();
      const cart = getQuickOrderCart(cartService.cart, getQuickOrderAssociateCustomerId());
      return !conditionedCartState(cart, keys).unmet;
    }

    _showRowError(text, container, duration = 3000) {
      if (!container) return;
      document.querySelectorAll('[data-js-form-error]').forEach(el => el.remove());
      const alert = document.createElement('div');
      alert.setAttribute('data-js-form-error', '');
      alert.style.width = '100%';
      alert.innerHTML = `<span class="form-error" style="margin:0;width:100%"><span class="body-size-3">${text}</span></span>`;
      container.parentElement.append(alert);
      container.classList.add('hide');
      setTimeout(() => { alert.remove(); container.classList.remove('hide'); }, duration);
    }
  }

  customElements.define('purchase-order-search-form', PurchaseOrderSearchForm);
}


// ============================================================
// SearchForm — Product search and filtering
// ============================================================

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();

      this.main = this.closest('purchase-order');
      this.input = this.querySelector('[data-js-search-input]');
      this.filterInputs = this.querySelectorAll('[data-js-search-filter-input]');
      this.filtersData = {};

      this.productList = this.main?.querySelector('[data-js-product-list]');
      this.searchResults = this.main?.querySelector('[data-js-search-results]');
      this.searchLoader = this.main?.querySelector('[data-js-search-loader]');

      this.a11yIndex = -1;
      this.results = [];

      this._setupListeners();
    }

    _setupListeners() {
      this.input?.addEventListener('keyup', debounce(() => {
        const q = this.input.value.trim();
        if (q) { this._search(q); } else { this._clearSearch(); }
      }, 200));

      this.input?.addEventListener('keydown', (e) => {
        if ([KEYCODES.UP, KEYCODES.DOWN, KEYCODES.RETURN].includes(e.keyCode)) e.preventDefault();
      });

      this.input?.addEventListener('keyup', (e) => {
        if (e.keyCode === KEYCODES.UP) return this._navigate('UP');
        if (e.keyCode === KEYCODES.DOWN) return this._navigate('DOWN');
      });

      this.filterInputs.forEach(input => {
        input.addEventListener('change', () => this._applyFilters());
      });
    }

    _search(query) {
      _showLoader(this.searchLoader);
      if (this.productList) this.productList.style.display = 'none';

      const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[options][fields]=title,variants.sku,variants.title&section_id=helper-purchase-order-search`;

      fetch(url)
        .then(res => { if (!res.ok) throw new Error(`Search failed: ${res.status}`); return res.text(); })
        .then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const section = doc.querySelector('#shopify-section-helper-purchase-order-search');
          if (!section) { this._renderEmpty(); return; }

          let filterJSON = null;
          const jsonEl = doc.querySelector('[data-js-search-results-helper-data]');
          if (jsonEl) { try { filterJSON = JSON.parse(jsonEl.innerHTML); } catch (e) { /* ignore */ } }

          if (filterJSON?.length && this._hasActiveFilters()) {
            const filtered = this._filterResults(filterJSON);
            const items = section.querySelectorAll('[data-js-search-item]');
            items.forEach(item => item.classList.add('hide'));
            filtered.forEach(r => {
              const el = section.querySelector(`[data-js-search-item][data-variant-id="${r.id}"]`);
              if (el) el.classList.remove('hide');
            });
          }

          if (this.searchResults) {
            this.searchResults.innerHTML = section.innerHTML;
            this.results = this.searchResults.querySelectorAll('[data-js-search-item]');
          }

          document.dispatchEvent(new CustomEvent('purchaseOrder:searchResultsUpdated'));

          if (!this.results.length) this._renderEmpty();

          this.searchResults?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        })
        .catch(err => { console.error('[Search]', err); this._renderEmpty(); })
        .finally(() => { _hideLoader(this.searchLoader); });
    }

    _clearSearch() {
      if (this.searchResults) this.searchResults.innerHTML = '';
      if (this.productList) this.productList.style.display = '';
      this.results = [];
      this.a11yIndex = -1;
      document.dispatchEvent(new CustomEvent('purchaseOrder:searchResultsUpdated'));
      if (this._hasActiveFilters()) this._applyFiltersToProductList();
    }

    _renderEmpty() {
      if (this.searchResults) {
        this.searchResults.innerHTML = `
          <div class="search-results">
            <div class="purchase-order-empty-state">
              <p>No se encontraron productos para tu búsqueda.</p>
            </div>
          </div>`;
      }
    }

    _applyFilters() {
      this.filterInputs.forEach(input => {
        const value = input.value;
        const option = this.querySelector(`[data-js-search-filters] [data-popout-option][data-value="${value}"]`);
        if (!option) return;
        const name = input.name;
        const rawValue = option.dataset.jsSearchFilterOption;
        let parsed = rawValue;
        if (rawValue === 'null') parsed = null;
        else if (rawValue === 'true') parsed = true;
        else if (rawValue === 'false') parsed = false;
        if (parsed === null) { delete this.filtersData[name]; } else { this.filtersData[name] = parsed; }
      });
      const q = this.input?.value?.trim();
      if (q) { this._search(q); } else { this._applyFiltersToProductList(); }
    }

    _applyFiltersToProductList() {
      if (!this.productList) return;
      const rows = this.productList.querySelectorAll('[data-js-search-item]');
      if (!this._hasActiveFilters()) { rows.forEach(row => row.classList.remove('hide')); return; }
      rows.forEach(row => {
        let show = true;
        if (this.filtersData.collections) {
          const collections = (row.dataset.collections || '').split(',');
          show = collections.includes(this.filtersData.collections);
        }
        if (show && typeof this.filtersData.available !== 'undefined') {
          show = (row.dataset.available === 'true') === this.filtersData.available;
        }
        row.classList.toggle('hide', !show);
      });
      this.productList.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    _hasActiveFilters() { return Object.keys(this.filtersData).length > 0; }

    _filterResults(data) {
      return data.filter(item => Object.entries(this.filtersData).every(([key, val]) => {
        if (Array.isArray(item[key])) return item[key].includes(val);
        return item[key] === val;
      }));
    }

    _navigate(direction) {
      const holder = document.querySelector('[data-js-search-results-holder]');
      if (this.a11yIndex === -1) { this.a11yIndex = 0; }
      else {
        if (this.results[this.a11yIndex]) this.results[this.a11yIndex].classList.remove('active');
        if (direction === 'UP' && this.a11yIndex > 0) this.a11yIndex--;
        else if (direction === 'DOWN' && this.a11yIndex < this.results.length - 1) this.a11yIndex++;
      }
      if (this.a11yIndex >= 0 && this.results[this.a11yIndex]) {
        this.results[this.a11yIndex].classList.add('active');
        if (holder) holder.scrollTop = this.results[this.a11yIndex].offsetTop - 200;
      } else if (holder) { holder.scrollTop = 0; }
    }
  }

  customElements.define('search-form', SearchForm);
}


// ============================================================
// QuantitySelector — Plus / minus / direct input
//
// Checks for busy state (is-busy class on purchase-order) and
// valid associate before allowing any changes.
// ============================================================

if (!customElements.get('quantity-selector')) {
  class QuantitySelector extends HTMLElement {
    constructor() {
      super();

      this.increaseBtn = this.querySelector('.quantity__button--plus');
      this.decreaseBtn = this.querySelector('.quantity__button--minus');
      this.input = this.querySelector('.quantity__input');
      this.maxQty = parseInt(this.getAttribute('variant-availability'), 10) || 0;

      this._prevValue = this.input?.value || '0';
      this.availabilityEl = this.closest('[data-js-purchase-order-search-form-row]')?.querySelector('.variant-availability');

      this.increaseBtn?.addEventListener('click', (e) => this._onIncrease(e));
      this.decreaseBtn?.addEventListener('click', (e) => this._onDecrease(e));
      this.input?.addEventListener('change', (e) => this._onInputChange(e));

      this._updateAvailabilityUI(forceInt(this.input?.value, 0));
    }

    connectedCallback() {
      applyConditionedRowLocks();
    }

    _isBusy() {
      return !!this.closest('purchase-order.is-busy');
    }

    _hasAssociate() {
      const hidden = document.querySelector('[purchase-order-associate]');
      if (hidden?.value?.trim()) return true;
      const txt = document.querySelector('[data-js-purchase-order-partner-update-success-text]');
      return !!(txt && txt.textContent.trim());
    }

    _showAssociateWarning() {
      const existing = document.querySelector('[data-js-associate-warning]');
      if (existing) return;
      const partnerInput = document.querySelector('[name="purchase_order[partner]"]');
      if (!partnerInput) return;
      partnerInput.classList.add('has-error');
      partnerInput.focus();
      const wrapper = partnerInput.closest('.purchase-order-partner');
      if (!wrapper) return;
      const warning = document.createElement('div');
      warning.setAttribute('data-js-associate-warning', '');
      warning.className = 'purchase-order-associate-warning';
      warning.innerHTML = `<span class="form-error" style="margin:0;padding:6px 10px;display:inline-block;">${_str('associate_required', 'Ingresa un código de asociado antes de agregar productos.')}</span>`;
      wrapper.parentElement.after(warning);
      setTimeout(() => { warning.remove(); partnerInput.classList.remove('has-error'); }, 4000);
    }

    _canInteract() {
      if (this._isBusy()) return false;
      if (!this._hasAssociate()) { this._showAssociateWarning(); return false; }
      return true;
    }

    _showNoStockWarning() {
      const row = this.closest('[data-js-purchase-order-search-form-row]');
      const container = row?.querySelector('.quantity__wrapper_error');
      if (!container) return;

      document.querySelectorAll('[data-js-no-stock-error]').forEach(el => el.remove());

      const alert = document.createElement('div');
      alert.setAttribute('data-js-no-stock-error', '');
      alert.style.width = '100%';
      alert.innerHTML = `<span class="form-error" style="margin:0;width:100%"><span class="body-size-3">${_str('no_more_stock', 'Ya no hay más productos disponibles para este artículo.')}</span></span>`;
      container.parentElement.append(alert);
      container.classList.add('hide');

      setTimeout(() => {
        alert.remove();
        container.classList.remove('hide');
      }, 3000);
    }

    _updateAvailabilityUI(currentQty) {
      if (!this.availabilityEl) return;

      const remaining = Math.max(this.maxQty - currentQty, 0);
      const status =
        remaining >= 5 ? 'available' :
          remaining > 0 ? 'available-low' : 'unavailable';

      this.availabilityEl.classList.remove('available', 'available-low', 'unavailable');
      this.availabilityEl.classList.add(status);

      const label = remaining > 0
        ? _str('available_text', 'Disponible')
        : _str('unavailable_text', 'Sin disponibilidad');
      const txt = this.availabilityEl.querySelector('span');
      if (txt) txt.textContent = label;
    }

    _onIncrease(e) {
      e.preventDefault();
      if (!this._canInteract()) return;
      let val = forceInt(this.input.value);
      if (val >= this.maxQty) {
        this._showNoStockWarning();
        return;
      }
      this._setValue(clamp(++val, 0, this.maxQty));
    }

    _onDecrease(e) {
      e.preventDefault();
      if (!this._canInteract()) return;
      let val = forceInt(this.input.value);
      this._setValue(clamp(--val, 0, this.maxQty));
    }

    _onInputChange(e) {
      e.preventDefault();
      if (!this._canInteract()) return;
      const requested = forceInt(this.input.value);
      const clamped = clamp(requested, 0, this.maxQty);
      if (requested > this.maxQty) {
        this._showNoStockWarning();
      }
      this._setValue(clamped);
    }

    _setValue(val) {
      this.input.value = val;
      this._updateAvailabilityUI(val);
      if (this._prevValue != val) {
        this._prevValue = val;
        this.input.dispatchEvent(new Event('QuickOrder:quantity:change', { bubbles: true }));
      }
    }

    syncFromExternal(val) {
      this.input.value = val;
      this._prevValue = String(val);
      this._updateAvailabilityUI(forceInt(val, 0));
    }
  }

  customElements.define('quantity-selector', QuantitySelector);
}
